import { apiFetch } from "@/lib/api";

export type LabelPurchaseOrderItem = {
  id: number;
  quantity: number;
  sku: string;
  description: string | null;
};

export type LabelPurchaseOrderRow = {
  id: number;
  number: string;
  created: string;
  mv_status: string | null;
  location_id: number | null;
  location_name: string | null;
  supplier_id: number | null;
  mv_supplier_name: string | null;
  active_csv: boolean;
  generation: number | null;
  items: LabelPurchaseOrderItem[];
};

export type LabelCsvRow = Record<string, string | number | boolean | null>;

export type LabelCsvCore = {
  error_msg?: string | null;
  is_reprint?: boolean;
  generation?: number | null;
  purchase_order_id?: number;
  purchase_order_number?: string | null;
  total_rows?: number;
};

export type LabelCsvFile = {
  filename: string;
  content: string;
  artId: number;
  artName: string;
  rowCount: number;
};

export type LabelSkuRow = {
  label_sku: string;
  product_sku: string;
  product_name: string | null;
  bar_code_model: string | null;
  art_id: number | null;
  active: boolean;
  art_name: string | null;
  label_type: string | null;
};

export type LabelSkuOption = {
  value: string;
  label: string;
};

export type ArtFileOption = {
  value: number;
  label: string;
};

export type ArtFileRow = {
  id: number;
  name: string;
  label_type: string | null;
  file_path: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  rules: unknown;
};

export type ProcessedModelDetails = {
  productLine: string | null;
  fullConfig: string | null;
  baseConfig: string | null;
  productName: string | null;
  barCodeModel: string | null;
  labelHasCheckbox: boolean;
  modelBoxName: string | null;
  boxBaseArtName: string | null;
  deviceLabelName: string | null;
  alreadyExists: boolean;
};

export async function listLabelPurchaseOrders(): Promise<LabelPurchaseOrderRow[]> {
  const response = await apiFetch<{ rows: LabelPurchaseOrderRow[] }>("/api/art-and-labels/purchase-orders");
  return response.rows;
}

export async function refreshLabelPurchaseOrders(): Promise<void> {
  await apiFetch<{ ok: boolean }>("/api/art-and-labels/purchase-orders/refresh", { method: "POST" });
}

export async function previewLabelPurchaseOrder(purchaseOrderId: number): Promise<{
  core: LabelCsvCore;
  rows: LabelCsvRow[];
}> {
  return apiFetch(`/api/art-and-labels/purchase-orders/${purchaseOrderId}/preview`);
}

export async function printLabelPurchaseOrder(purchaseOrderId: number): Promise<{
  core: LabelCsvCore;
  files: LabelCsvFile[];
}> {
  return apiFetch(`/api/art-and-labels/purchase-orders/${purchaseOrderId}/print`, { method: "POST" });
}

export async function voidLabelPurchaseOrder(purchaseOrderId: number): Promise<void> {
  await apiFetch(`/api/art-and-labels/purchase-orders/${purchaseOrderId}/void`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function listLabelSkus(): Promise<LabelSkuRow[]> {
  const response = await apiFetch<{ rows: LabelSkuRow[] }>("/api/art-and-labels/label-skus");
  return response.rows;
}

export async function listPotentialLabelSkus(): Promise<LabelSkuOption[]> {
  const response = await apiFetch<{ options: LabelSkuOption[] }>("/api/art-and-labels/label-skus/potential");
  return response.options;
}

export async function listArtFileOptions(): Promise<ArtFileOption[]> {
  const response = await apiFetch<{ options: ArtFileOption[] }>("/api/art-and-labels/label-skus/art-options");
  return response.options;
}

export async function createLabelSku(input: {
  labelSku: string;
  productSku: string;
  artId: number;
}): Promise<void> {
  await apiFetch("/api/art-and-labels/label-skus", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateLabelSku(
  labelSku: string,
  input: { active: boolean; artId: number },
): Promise<void> {
  await apiFetch(`/api/art-and-labels/label-skus/${encodeURIComponent(labelSku)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function listArtFiles(): Promise<ArtFileRow[]> {
  const response = await apiFetch<{ rows: ArtFileRow[] }>("/api/art-and-labels/art-files");
  return response.rows;
}

export async function listValidDeviceSkus(): Promise<string[]> {
  const response = await apiFetch<{ skus: string[] }>("/api/art-and-labels/models/device-skus");
  return response.skus;
}

export async function previewNewModel(model: string): Promise<ProcessedModelDetails> {
  const response = await apiFetch<{ details: ProcessedModelDetails }>(
    `/api/art-and-labels/models/preview?model=${encodeURIComponent(model)}`,
  );
  return response.details;
}

export async function createLabelInfoModel(input: {
  model: string;
  barCodeModel: string;
  boxBaseArtName: string;
  sku: string;
  modelBox: string;
  modelDevice: string;
}): Promise<void> {
  await apiFetch("/api/art-and-labels/models", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function downloadCsvFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function formatPurchaseOrderCreated(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}
